import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Database, GitBranch, Download, Zap } from 'lucide-react';

export const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Database className="w-8 h-8 text-primary" strokeWidth={1.5} />
              <span className="text-2xl font-bold tracking-tight" style={{fontFamily: 'Space Grotesk'}}>DBCanvas - CSKU.ai</span>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                data-testid="nav-login-btn"
                variant="ghost"
                onClick={() => navigate('/login')}
                className="hover:-translate-y-[1px] transition-transform duration-200"
              >
                Login
              </Button>
              <Button
                data-testid="nav-register-btn"
                onClick={() => navigate('/register')}
                className="hover:-translate-y-[1px] transition-transform duration-200 shadow-md"
              >
                Start Designing Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <section className="relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1765046255517-412341954c4c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMG5ldHdvcmslMjB3aGl0ZSUyMGJhY2tncm91bmQlMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc2NTU5OTEyNHww&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center">
            <h1 
              className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-none mb-6"
              style={{fontFamily: 'Space Grotesk'}}
            >
              Design Databases at the
              <span className="text-primary block mt-2">Speed of Thought</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-12">
              A modern visual database schema designer. Create tables, define relationships, and export production-ready MySQL schemas with zero friction.
            </p>
            <Button
              data-testid="hero-cta-btn"
              size="lg"
              onClick={() => navigate('/register')}
              className="text-lg px-8 py-6 hover:-translate-y-[1px] transition-transform duration-200 shadow-xl"
            >
              Start Designing Free
            </Button>
          </div>
        </div>
      </section>

      <section className="py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-center mb-16" style={{fontFamily: 'Space Grotesk'}}>
            Built for Modern Developers
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-6 border border-border rounded-lg bg-card hover:shadow-lg transition-shadow duration-200">
              <div className="w-12 h-12 bg-primary/10 rounded-md flex items-center justify-center mb-4">
                <Database className="w-6 h-6 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-semibold mb-3">Visual Table Designer</h3>
              <p className="text-base text-muted-foreground leading-relaxed">
                Drag and drop tables on an infinite canvas. Define columns with all MySQL data types, constraints, and properties.
              </p>
            </div>
            <div className="p-6 border border-border rounded-lg bg-card hover:shadow-lg transition-shadow duration-200">
              <div className="w-12 h-12 bg-primary/10 rounded-md flex items-center justify-center mb-4">
                <GitBranch className="w-6 h-6 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-semibold mb-3">Smart Relationships</h3>
              <p className="text-base text-muted-foreground leading-relaxed">
                Create relationships by dragging between columns. Support for One-to-One, One-to-Many, Many-to-Many with CASCADE rules.
              </p>
            </div>
            <div className="p-6 border border-border rounded-lg bg-card hover:shadow-lg transition-shadow duration-200">
              <div className="w-12 h-12 bg-primary/10 rounded-md flex items-center justify-center mb-4">
                <Download className="w-6 h-6 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-semibold mb-3">Export MySQL SQL</h3>
              <p className="text-base text-muted-foreground leading-relaxed">
                Generate production-ready SQL with CREATE TABLE, FOREIGN KEY, and INDEX statements. Import directly to MySQL.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Zap className="w-8 h-8 text-primary" strokeWidth={1.5} />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6" style={{fontFamily: 'Space Grotesk'}}>
              Start Building Your Database Schema
            </h2>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-8">
              Join developers who design better databases with visual tools.
            </p>
            <Button
              data-testid="bottom-cta-btn"
              size="lg"
              onClick={() => navigate('/register')}
              className="text-lg px-8 py-6 hover:-translate-y-[1px] transition-transform duration-200 shadow-xl"
            >
              Get Started Now
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
            <Database className="w-4 h-4" strokeWidth={1.5} />
            <span>DBCanvas - CSKU.ai © 2025 - Visual Database Designer</span>
          </div>
        </div>
      </footer>
    </div>
  );
};
